import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import OverflowingText from '../../components/foundations/OverflowingText';
import EditableTitle from '../../components/widgets/EditableTitle';
import { Button } from '../../components/widgets';
import GeneralPopup, { PopupTypes } from '../../components/organisms/GeneralPopup';
import { SESSION_TITLE_PLACEHOLDER_MAX_LENGTH } from '../constants';
import css from './SessionList.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.SESSION_LIST';

const SessionList = ({
    sessions,
    currentSessionId,
    loadingSessionId,
    streamingSessionIds = [],
    onSessionSelect,
    onSessionDelete,
    onSessionUpdate,
    isLoading,
    loadError,
    onRetry,
    translate,
}) => {
    const streamingSet = new Set(streamingSessionIds);
    const [editingSessionId, setEditingSessionId] = useState(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [savingSessionId, setSavingSessionId] = useState(null);
    const [confirmDeleteSessionId, setConfirmDeleteSessionId] = useState();
    const [renameError, setRenameError] = useState(null);

    const onDelete = (e, sessionId) => {
        e.stopPropagation();
        setConfirmDeleteSessionId(sessionId);
    };

    const onDeleteConfirm = () => {
        const sessionId = confirmDeleteSessionId;
        setConfirmDeleteSessionId(null);
        if (sessionId) onSessionDelete(sessionId);
    };

    const onDeleteCancel = () => {
        setConfirmDeleteSessionId(null);
    };

    const onTitleEdit = (e, sessionId) => {
        e.stopPropagation();
        const session = sessions.find(s => s.session_id === sessionId);
        if (session) {
            setEditingTitle(session.title);
            setEditingSessionId(sessionId);
            setRenameError(null);
        }
    };

    const onTitleChange = (sessionId, newTitle) => {
        setEditingTitle(newTitle);
        if (renameError) setRenameError(null);
    };

    const handleTitleChangeComplete = async sessionId => {
        const trimmedTitle = editingTitle.trim();

        if (!trimmedTitle) {
            // Empty input: stay in edit mode and surface a visible error.
            setRenameError('STATIC.PAGES.ANALYSIS_CHAT.SESSION_LIST.RENAME_EMPTY_ERROR');
            return;
        }

        if (savingSessionId === sessionId) return;

        if (!onSessionUpdate) {
            setEditingSessionId(null);
            setEditingTitle('');
            setRenameError(null);
            return;
        }

        setSavingSessionId(sessionId);
        const success = await onSessionUpdate(sessionId, { title: trimmedTitle });
        setSavingSessionId(null);

        if (success) {
            setEditingSessionId(null);
            setEditingTitle('');
            setRenameError(null);
        }
    };

    const onSessionItemClick = (e, sessionId) => {
        if (loadingSessionId) return;
        if (editingSessionId === sessionId) return;

        onSessionSelect(sessionId);
    };

    return (
        <>
            <div className={css.sessionListContainer}>
                {isLoading ? (
                    <div className={css.message}>
                        <Translate id={`${TRANSLATION_PREFIX}.LOADING`} />
                    </div>
                ) : loadError ? (
                    <div className={css.errorContainer}>
                        <div className={css.message}>
                            <Translate id={`${TRANSLATION_PREFIX}.LOAD_ERROR`} />
                        </div>
                        {onRetry && (
                            <Button onClick={onRetry}>
                                <Translate id={`${TRANSLATION_PREFIX}.RETRY`} />
                            </Button>
                        )}
                    </div>
                ) : !sessions?.length ? (
                    <div className={css.message}>
                        <Translate id={`${TRANSLATION_PREFIX}.EMPTY`} />
                    </div>
                ) : (
                    sessions.map(session => {
                        const isActive = session.session_id === currentSessionId;
                        const isEditing = editingSessionId === session.session_id;
                        const isLoadingSession = session.session_id === loadingSessionId;
                        const isSavingSession = savingSessionId === session.session_id;
                        const isStreamingSession = streamingSet.has(session.session_id);
                        const showInlineSpinner = isLoadingSession || isStreamingSession;

                        return (
                            <div
                                key={session.session_id}
                                className={classNames(css.sessionItem, {
                                    [css.active]: isActive,
                                    [css.edit]: isEditing,
                                    [css.loading]: isLoadingSession,
                                })}
                                onClick={e => onSessionItemClick(e, session.session_id)}
                            >
                                <div className={css.sessionItemContent}>
                                    <div className={css.sessionItemTitle}>
                                        {isEditing ? (
                                            <div className={css.sessionLoadingTitle}>
                                                <EditableTitle
                                                    value={editingTitle}
                                                    placeholder={translate(
                                                        `${TRANSLATION_PREFIX}.UNTITLED_PLACEHOLDER`
                                                    )}
                                                    onTextChanged={value => onTitleChange(session.session_id, value)}
                                                    onTextChangeComplete={() =>
                                                        handleTitleChangeComplete(session.session_id)
                                                    }
                                                    containerClass={css.editableTitleContainer}
                                                    selectOnFocus
                                                    maxLength={SESSION_TITLE_PLACEHOLDER_MAX_LENGTH}
                                                    error={renameError ? translate(renameError) : null}
                                                />
                                                {isSavingSession && (
                                                    <Icon
                                                        name="loader"
                                                        size={IconSizes.X_SMALL}
                                                        className={css.sessionSpinner}
                                                    />
                                                )}
                                            </div>
                                        ) : showInlineSpinner ? (
                                            <div className={css.sessionLoadingTitle}>
                                                <Icon
                                                    name="loader"
                                                    size={IconSizes.X_SMALL}
                                                    className={css.sessionSpinner}
                                                />
                                                <OverflowingText>{session.title}</OverflowingText>
                                            </div>
                                        ) : (
                                            <OverflowingText>{session.title}</OverflowingText>
                                        )}
                                    </div>
                                    <div className={css.sessionItemActions}>
                                        <Icon
                                            name="edit"
                                            className={css.sessionItemActionButton}
                                            onClick={e => onTitleEdit(e, session.session_id)}
                                            hoverText={`${TRANSLATION_PREFIX}.EDIT_TITLE_HOVER`}
                                        />
                                        <Icon
                                            name="trash"
                                            className={css.sessionItemActionButton}
                                            onClick={e => onDelete(e, session.session_id)}
                                            hoverText={`${TRANSLATION_PREFIX}.DELETE_HOVER`}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            <GeneralPopup
                open={!!confirmDeleteSessionId}
                type={PopupTypes.DELETE}
                title={`${TRANSLATION_PREFIX}.DELETE_POPUP.TITLE`}
                text={`${TRANSLATION_PREFIX}.DELETE_POPUP.CONTENT`}
                acceptText={`${TRANSLATION_PREFIX}.DELETE_POPUP.ACCEPT_BUTTON`}
                rejectText={`${TRANSLATION_PREFIX}.DELETE_POPUP.REJECT_BUTTON`}
                onAccept={onDeleteConfirm}
                onReject={onDeleteCancel}
            />
        </>
    );
};

SessionList.propTypes = {
    sessions: PropTypes.arrayOf(
        PropTypes.shape({
            session_id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            message_count: PropTypes.number.isRequired,
        })
    ).isRequired,
    currentSessionId: PropTypes.string,
    loadingSessionId: PropTypes.string,
    streamingSessionIds: PropTypes.arrayOf(PropTypes.string),
    onSessionSelect: PropTypes.func.isRequired,
    onSessionDelete: PropTypes.func.isRequired,
    onSessionUpdate: PropTypes.func,
    isLoading: PropTypes.bool,
    loadError: PropTypes.bool,
    onRetry: PropTypes.func,
    translate: PropTypes.func,
};

export default withLocalize(SessionList);
